import ReactDom from 'react-dom';
import { Button } from 'antd';
import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrgValue } from 'hooks/business/system/useOrg';
import useRoles from 'hooks/business/useRoles';
import usePromoteDays from 'hooks/business/usePromoteDays';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import useLayerLock from 'hooks/useLayerLock';
import { hidePayment } from 'services/chattt';
import PromoteContent from './PromoteContent';

import bgPic from './subscribeBubble.webp';
import quickBgPic from './subscribeBubble-quick.png';
import {
  getExpireDate,
  getUsedDay,
  useIncentiveValue,
  useShowIncentive,
} from 'views/Management/NewuserIncentive/state';
import { useActivityTypeLabel } from 'views/Management/NewuserIncentive';
import { useFlagConfigValue } from 'hooks/business/system/useFlagConfig';
import { isMasterOnline, isQuickChat, isSiteGiant } from 'productLine';
import dayjs from 'lib/dayjs';
import { T, useTranslate } from '@tolgee/react';
import { useQuery } from '@tanstack/react-query';
import { queries } from 'services/queries';

/**
 * 因为不需要共享，暂时使用scoped单例状态，防止重复render时多次打开
 */
const singletonState = {
  visible: true,
};

const SubscribeModal = () => {
  const {
    gained: layerLockGained,
    acquire: acquireLayerLock,
    release: releaseLayerLock,
  } = useLayerLock('SubscribeModal', 100);

  // 在距离结束前多少天进行提示
  const { subscribeModalWaringDaysBeforeExpired = 3 } = useFlagConfigValue();
  const { inPromoteRange, skipModal } = usePromoteDays();
  const lang = useSystemLanguageValue();
  const { t } = useTranslate();
  const nav = useNavigate();
  const { isSuperviser } = useRoles();
  const orgValue = useOrgValue();
  const isExpired = orgValue?.isExpired;
  const expiredInDays = orgValue?.expiredInDays;

  const [visible, setVisible] = useState(singletonState.visible);
  const [workingPack, setWrokingPack] = useState<Pay.OrderedPackage>();
  const showDays = expiredInDays ?? 0;
  const inExpiredRange =
    expiredInDays !== undefined && expiredInDays <= subscribeModalWaringDaysBeforeExpired;
  const actualVisible = visible && (skipModal ? inExpiredRange : inExpiredRange || inPromoteRange);
  const shouldShow = !hidePayment && layerLockGained && actualVisible && workingPack;
  const isToday = dayjs(orgValue?.activeExpireTimestamp).isSame(dayjs(), 'day');

  const { data: orderedPackageData } = useQuery({
    ...queries.accounts.orderedPackage,
    enabled: !!orgValue,
  });

  useEffect(() => {
    if (orderedPackageData) {
      const workingPack = orderedPackageData.find((v) => v.status === 1);
      setWrokingPack(workingPack);
    }
  }, [orderedPackageData]);

  useEffect(() => {
    singletonState.visible = visible;
    if (!visible) {
      releaseLayerLock();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);
  useEffect(() => {
    if (!actualVisible) {
      releaseLayerLock();

      return () => {};
    }

    acquireLayerLock();

    return () => {
      releaseLayerLock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualVisible]);
  const titleCss = 'leading-[37px] text-[#2f42f8] text-[26px] font-semibold';
  if (isMasterOnline || isSiteGiant) return null;
  return shouldShow
    ? ReactDom.createPortal(
        <>
          <FramelessModal>
            <Wrapper mode={lang === 'zh-CN' ? undefined : 'compat'}>
              <Body>
                {inPromoteRange && !skipModal ? (
                  <PromoteContent />
                ) : (
                  <FlexBox>
                    {isExpired ? (
                      isQuickChat ? (
                        <div className={titleCss}>{t('quickchat_expired')}</div>
                      ) : (
                        <H2>
                          {t(`套餐.${workingPack!.id}`)}
                          {t('subscribe.thinExpired')}
                        </H2>
                      )
                    ) : isQuickChat ? (
                      // 今天
                      isToday ? (
                        <div className={titleCss}>{t('quickchat_today_expired')}</div>
                      ) : (
                        <div className={titleCss}>
                          {t('quickchat_dueexpire_title', { x: showDays })}
                        </div>
                      )
                    ) : (
                      <H2>
                        {t('subscribe.仅剩N天，使用即将到期', {
                          days: showDays <= 0 ? 0 : showDays,
                        })}
                      </H2>
                    )}
                    <Desc className="mt-[6px] text-[16px] leading-[22px] text-[#515359]">
                      {t('quickchat_dueexpire_subtitle')}
                    </Desc>
                  </FlexBox>
                )}
                <FlexBox>
                  {isSuperviser && !isSiteGiant && !isMasterOnline ? (
                    <SubBtn
                      onClick={() => {
                        nav('/management/service/market');
                        setVisible(false);
                      }}
                    >
                      {t('subscribe.subscribe.button')}
                    </SubBtn>
                  ) : null}
                  <TextButton onClick={() => setVisible(false)} type="link">
                    {t('subscribe.下次提醒')}
                  </TextButton>
                </FlexBox>
              </Body>
            </Wrapper>
          </FramelessModal>
          <Mask />
        </>,
        document.body,
      )
    : null;
};

export default function SubScribeModalWithIncetive() {
  const { t, activityTypeLabel } = useActivityTypeLabel();

  const nav = useNavigate();

  const incentiveValue = useIncentiveValue();

  const showIncentive = useShowIncentive();

  const [visible, setVisible] = useState(singletonState.visible);

  const onClose = () => {
    setVisible(false);
    singletonState.visible = false;
  };

  if (!incentiveValue) {
    return null;
  }

  const { activityExpireDay, orderTotal, channelOrderLimit, activityStartTimestamp } =
    incentiveValue;

  const displayIncentive = visible && showIncentive && activityExpireDay <= 7;

  if (!displayIncentive) {
    return <SubscribeModal />;
  }

  return ReactDom.createPortal(
    <>
      <FramelessModal>
        <Wrapper>
          <Body
            style={{
              padding: '10px 20px',
              justifyContent: 'space-around',
            }}
          >
            <div
              style={{
                fontSize: '16px',
                fontWeight: '500',
                textAlign: 'center',
                color: '#192544',
              }}
            >
              {orderTotal > channelOrderLimit
                ? t('incentive.店铺近30日付款订单>x单，已不符合y的参与条件', {
                    x: channelOrderLimit,
                    y: activityTypeLabel,
                  })
                : t('incentive.参与x活动已y天', {
                    x: activityTypeLabel,
                    y: getUsedDay(activityStartTimestamp),
                  })}
            </div>
            <div
              style={{
                padding: '8px 38px',
                textAlign: 'center',
                background: '#E1EDFF',
                borderRadius: '2px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '16px',
                  fontWeight: '500',
                  color: '#192544',
                }}
              >
                <T
                  keyName="incentive.免费体验仅剩x天"
                  params={{
                    x: (
                      <span
                        style={{
                          margin: '0 6px',
                          fontSize: '20px',
                          color: '#FD6032',
                        }}
                      />
                    ),
                    count: activityExpireDay,
                  }}
                />
              </div>
              <span style={{ color: '#84868C' }}>
                {t('incentive.有效期至：x', { x: getExpireDate(activityExpireDay) })}
              </span>
            </div>
            <div
              style={{
                fontSize: '16px',
                fontWeight: '500',
                color: '#FF3F4B',
              }}
            >
              {t('incentive.为您准备了专属优惠')}
            </div>
            {isSiteGiant || isMasterOnline ? null : (
              <SubBtn
                style={{ margin: 0 }}
                onClick={() => {
                  nav('/management/service/market');
                  onClose();
                }}
              >
                {t('incentive.优惠订购')}
              </SubBtn>
            )}
            <TextButton style={{ padding: 0 }} onClick={onClose} type="link">
              {t('subscribe.下次提醒')}
            </TextButton>
          </Body>
        </Wrapper>
      </FramelessModal>
      <Mask />
    </>,
    document.body,
  );
}

export const FramelessModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 99;
`;
export const Mask = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #000;
  opacity: 0.4;
  z-index: 98;
`;
export const Wrapper = styled.div<{ mode?: 'compat' }>`
  background-size: 100% 100%;
  background-image: url(${`"${isQuickChat ? quickBgPic : bgPic}"`});
  box-sizing: border-box;
  ${({ mode }) => {
    if (mode === 'compat') {
      return {
        minWidth: '686px',
        minHeight: '348px',
        padding: '51px 24px 17px 112px',
        borderRadius: '20px',
        backgroundSize: 'cover',
      };
    }
    return {
      width: '686px',
      minHeight: '348px',
      padding: '51px 24px 17px 112px',
    };
  }}
`;
export const Body = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 280px;
  padding: 37px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  background-color: #fff;
  border-radius: 20px;
  box-sizing: border-box;
  color: #515359;
`;
export const FlexBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
export const H2 = styled.h2`
  color: #f8482f;
  font-size: 26px;
  margin-bottom: 0;
  line-height: 37px;
`;
export const SubBtn = styled.a`
  width: 248px;
  height: 36px;
  margin: 20px 0 8px 0;
  line-height: 36px;
  text-align: center;
  display: block;
  background: ${isQuickChat ? '#6954f0' : 'linear-gradient(to right bottom, #f7442e, #fd6332)'};
  border-radius: 20px;
  color: #fff !important;
  box-shadow: ${isQuickChat
    ? '0px 4px 8px 0px rgba(42,0,174,0.19)'
    : '0px 4px 8px 0px rgba(174, 30, 0, 0.19)'};
`;

export const TextButton = styled(Button)`
  color: #84868c;
`;

const Desc = styled.div`
  color: #515359;
  font-size: 16px;
  margin-top: 13px;
`;
